import { useEffect, useRef, useState } from "react";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@400;500;600;700&family=Orbitron:wght@400;600;700;900&display=swap');

  .edu-section {
    position: relative;
    z-index: 1;
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 100px 4rem 80px;
    font-family: 'Rajdhani', sans-serif;
  }

  .edu-container {
    width: 100%;
    max-width: 1100px;
    margin: 0 auto;
  }

  /* HEADER */
  .edu-header {
    text-align: center;
    margin-bottom: 4rem;
  }

  .edu-label {
    font-family: 'Orbitron', monospace;
    font-size: 0.65rem;
    font-weight: 600;
    color: #ff8c00;
    letter-spacing: 5px;
    text-transform: uppercase;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    margin-bottom: 1rem;
    opacity: 0;
    animation: eduFadeUp 0.7s ease 0.1s both;
  }
  .edu-label::before, .edu-label::after { content: ''; width: 50px; height: 1px; }
  .edu-label::before { background: linear-gradient(to right, transparent, #ff8c00); }
  .edu-label::after  { background: linear-gradient(to left,  transparent, #ff8c00); }

  .edu-title {
    font-family: 'Orbitron', monospace;
    font-size: clamp(1.8rem, 4vw, 2.8rem);
    font-weight: 900;
    background: linear-gradient(135deg, #ffffff 0%, #38c8ff 60%, #00aaff 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    filter: drop-shadow(0 0 20px rgba(0,170,255,0.3));
    opacity: 0;
    animation: eduFadeUp 0.7s ease 0.2s both;
  }

  /* MAIN LAYOUT */
  .edu-layout {
    display: grid;
    grid-template-columns: 1fr 1.6fr;
    gap: 2rem;
    align-items: start;
  }

  /* TIMELINE */
  .edu-timeline {
    position: relative;
    opacity: 0;
    animation: eduFadeUp 0.7s ease 0.3s both;
  }

  .timeline-line {
    position: absolute;
    left: 16px;
    top: 0;
    bottom: 0;
    width: 1px;
    background: linear-gradient(to bottom, #00aaff, rgba(0,170,255,0.1));
  }

  .timeline-item {
    position: relative;
    padding-left: 48px;
    margin-bottom: 2rem;
    cursor: pointer;
    transition: transform 0.2s;
  }

  .timeline-item:hover { transform: translateX(4px); }

  .timeline-dot {
    position: absolute;
    left: 8px;
    top: 6px;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    border: 2px solid #00aaff;
    background: #020810;
    box-shadow: 0 0 10px rgba(0,170,255,0.5);
    transition: background 0.3s, box-shadow 0.3s;
    z-index: 1;
  }

  .timeline-item.active .timeline-dot,
  .timeline-item:hover .timeline-dot {
    background: #00aaff;
    box-shadow: 0 0 16px rgba(0,170,255,0.8);
  }

  .timeline-item.current .timeline-dot {
    border-color: #ff8c00;
    box-shadow: 0 0 12px rgba(255,140,0,0.6);
  }

  .timeline-item.current.active .timeline-dot,
  .timeline-item.current:hover .timeline-dot {
    background: #ff8c00;
  }

  .timeline-years {
    font-family: 'Orbitron', monospace;
    font-size: 0.65rem;
    color: #4a7a9b;
    letter-spacing: 1px;
    margin-bottom: 0.3rem;
  }

  .timeline-item.current .timeline-years { color: #ffaa33; }

  .timeline-school {
    font-family: 'Orbitron', monospace;
    font-size: 0.8rem;
    font-weight: 700;
    color: #e8f4ff;
    margin-bottom: 0.2rem;
    line-height: 1.3;
  }

  .timeline-degree {
    font-size: 0.85rem;
    color: rgba(232,244,255,0.5);
  }

  .current-badge {
    display: inline-block;
    background: rgba(255,140,0,0.12);
    border: 1px solid rgba(255,140,0,0.4);
    border-radius: 10px;
    padding: 0.15rem 0.6rem;
    font-size: 0.6rem;
    font-family: 'Orbitron', monospace;
    color: #ffaa33;
    letter-spacing: 1px;
    margin-left: 0.5rem;
    vertical-align: middle;
  }

  /* DETAIL CARD */
  .edu-detail {
    opacity: 0;
    animation: eduFadeUp 0.7s ease 0.4s both;
  }

  .edu-card {
    background: rgba(0, 20, 50, 0.5);
    border: 1px solid rgba(0,170,255,0.15);
    border-top: 2px solid #00aaff;
    padding: 2rem;
    position: relative;
    transition: border-color 0.3s;
  }

  .edu-card.orange-top { border-top-color: #ff8c00; }

  .edu-card::before {
    content: '';
    position: absolute;
    top: -2px; left: 0; right: 0;
    height: 2px;
    background: linear-gradient(to right, #00aaff, transparent);
  }

  .edu-card.orange-top::before {
    background: linear-gradient(to right, #ff8c00, transparent);
  }

  .card-school-name {
    font-family: 'Orbitron', monospace;
    font-size: 1rem;
    font-weight: 900;
    color: #e8f4ff;
    margin-bottom: 0.3rem;
    line-height: 1.3;
  }

  .card-degree {
    font-size: 0.95rem;
    color: #00aaff;
    font-weight: 600;
    margin-bottom: 0.2rem;
  }

  .card-degree.orange { color: #ffaa33; }

  .card-years {
    font-family: 'Orbitron', monospace;
    font-size: 0.7rem;
    color: #4a7a9b;
    letter-spacing: 1px;
    margin-bottom: 1.5rem;
  }

  .card-divider {
    height: 1px;
    background: linear-gradient(to right, rgba(0,170,255,0.3), transparent);
    margin-bottom: 1.2rem;
  }

  .card-section-title {
    font-family: 'Orbitron', monospace;
    font-size: 0.6rem;
    color: #00aaff;
    letter-spacing: 3px;
    text-transform: uppercase;
    margin-bottom: 0.8rem;
  }

  .card-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 1.2rem;
  }

  .card-tag {
    background: rgba(0,170,255,0.07);
    border: 1px solid rgba(0,170,255,0.2);
    border-radius: 4px;
    padding: 0.25rem 0.7rem;
    font-size: 0.78rem;
    color: rgba(232,244,255,0.6);
    transition: border-color 0.3s, color 0.3s;
  }

  .card-tag:hover {
    border-color: rgba(0,170,255,0.5);
    color: #e8f4ff;
  }

  .card-tag.orange-tag {
    background: rgba(255,140,0,0.06);
    border-color: rgba(255,140,0,0.2);
    color: rgba(255,200,100,0.7);
  }

  .card-desc {
    font-size: 0.9rem;
    line-height: 1.8;
    color: rgba(232,244,255,0.5);
  }

  /* STATS ROW */
  .edu-stats {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1rem;
    margin-top: 2rem;
    opacity: 0;
    animation: eduFadeUp 0.7s ease 0.6s both;
  }

  .edu-stat {
    background: rgba(0,20,50,0.5);
    border: 1px solid rgba(0,170,255,0.12);
    padding: 1rem;
    text-align: center;
    position: relative;
  }

  .edu-stat::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 1px;
    background: linear-gradient(to right, transparent, rgba(0,170,255,0.4), transparent);
  }

  .stat-val {
    font-family: 'Orbitron', monospace;
    font-size: 1.2rem;
    font-weight: 700;
    color: #ffaa33;
    display: block;
  }

  .stat-lbl {
    font-size: 0.7rem;
    color: #4a7a9b;
    letter-spacing: 1px;
    text-transform: uppercase;
    margin-top: 0.3rem;
    display: block;
  }

  @keyframes eduFadeUp {
    from { opacity: 0; transform: translateY(24px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  @media (max-width: 900px) {
    .edu-section { padding: 100px 2rem 60px; }
    .edu-layout { grid-template-columns: 1fr; }
    .edu-stats { grid-template-columns: repeat(3, 1fr); }
  }

  @media (max-width: 500px) {
    .edu-stats { grid-template-columns: repeat(2, 1fr); }
  }
`;

const EDU_DATA = [
  {
    id: 0,
    years: '2023 – Present',
    school: 'First Asia Institute of Technology and Humanities',
    shortName: 'FAITH Colleges',
    degree: 'Bachelor of Science in Computer Engineering',
    current: true,
    yearLevel: '3rd Year',
    focus: ['Artificial Intelligence', 'Internet of Things', 'Embedded Systems'],
    coursework: ['Data Structures & Algorithms', 'Digital Logic Design', 'System Verilog / RTL', 'Embedded Systems' ],
    desc: 'Currently pursuing BS in Computer Engineering with a focus on AI and IoT. Engaged in both hardware and software projects, including a mini-thesis on a glove-controlled robot arm using ESP32.',
  },
  {
    id: 1,
    years: '2021 – 2023',
    school: 'Malvar Senior High School',
    shortName: 'Malvar SHS',
    degree: 'Senior High School – Science, Technology, Engineering & Mathematics',
    current: false,
    focus: ['STEM Track', 'Engineering Sciences', 'Research'],
    coursework: ['General Mathematics', 'Pre-Calculus', 'Basic Calculus', 'General Physics', 'Capstone', 'Genearal Chemistry'],
    desc: 'Completed the STEM strand which built the foundation for engineering concepts, scientific research, and analytical problem-solving. Developed a ball-shaped automatic fire extinguisher utilizing a thermally-activated eggshell mechanism that ruptures upon heat exposure, releasing dry chemical suppression powder to rapidly extinguish fire without manual operation. ',
  },
  {
    id: 2,
    years: '2017 – 2021',
    school: 'De La Salle Lipa',
    shortName: 'De La Salle Lipa',
    degree: 'Junior High School',
    current: false,
    focus: ['Science & Technology', 'Mathematics', 'Computer Fundamentals'],
    coursework: ['Mathematics', 'Science', 'Technology & Livelihood Education', 'English', 'Filipino'],
    desc: 'Built a strong academic foundation in science and mathematics at one of the leading secondary schools in Batangas.',
  },
  {
    id: 3,
    years: '2011 – 2017',
    school: 'Immaculate Conception Montessori School',
    shortName: 'ICMS',
    degree: 'Primary School',
    current: false,
    focus: ['Core Academics', 'Holistic Development', 'Values Formation'],
    coursework: ['Mathematics', 'Science', 'English'],
    desc: 'Received a well-rounded primary education with emphasis on values formation, core academics, and holistic development.',
  },
];

export default function Education() {
  const [active, setActive] = useState(0);
  const selected = EDU_DATA[active];

  return (
    <section className="edu-section" id="education">
      <style>{styles}</style>
      <div className="edu-container">

        {/* Header */}
        <div className="edu-header">
          <p className="edu-label">Academic Background</p>
          <h2 className="edu-title">Education</h2>
        </div>

        {/* Layout */}
        <div className="edu-layout">

          {/* Timeline */}
          <div className="edu-timeline">
            <div className="timeline-line" />
            {EDU_DATA.map((edu) => (
              <div
                key={edu.id}
                className={`timeline-item ${edu.current ? 'current' : ''} ${active === edu.id ? 'active' : ''}`}
                onClick={() => setActive(edu.id)}
              >
                <div className="timeline-dot" />
                <p className="timeline-years">{edu.years}</p>
                <p className="timeline-school">
                  {edu.shortName}
                  {edu.current && <span className="current-badge">CURRENT</span>}
                </p>
                <p className="timeline-degree">{edu.degree}</p>
              </div>
            ))}
          </div>

          {/* Detail Card */}
          <div className="edu-detail">
            <div className={`edu-card ${selected.current ? 'orange-top' : ''}`} key={selected.id}>
              <p className="card-school-name">{selected.school}</p>
              <p className={`card-degree ${selected.current ? 'orange' : ''}`}>{selected.degree}</p>
              <p className="card-years">
                {selected.years}
                {selected.current && ` · ${selected.yearLevel}`}
              </p>

              <div className="card-divider" />

              {/* Focus Areas */}
              <p className="card-section-title">// Focus Areas</p>
              <div className="card-tags">
                {selected.focus.map((f, i) => (
                  <span key={i} className={`card-tag ${selected.current ? 'orange-tag' : ''}`}>{f}</span>
                ))}
              </div>

              {/* Coursework */}
              <p className="card-section-title">// Relevant Coursework</p>
              <div className="card-tags">
                {selected.coursework.map((c, i) => (
                  <span key={i} className="card-tag">{c}</span>
                ))}
              </div>

              <div className="card-divider" />

              <p className="card-desc">{selected.desc}</p>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="edu-stats">
          {[
            { val: '3rd', lbl: 'Year Level' },
            { val: '2023', lbl: 'Year Started' },
            { val: 'BSCPE', lbl: 'Program' },
          ].map((s, i) => (
            <div className="edu-stat" key={i}>
              <span className="stat-val">{s.val}</span>
              <span className="stat-lbl">{s.lbl}</span>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}